/**
 * TenantProvidersPage
 * 
 * Page for managing tenant-owned LLM providers (BYOM - Bring Your Own Model).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Loader2, AlertTriangle, Key } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getErrorMessage } from '@/lib/api-client';
import { useI18n } from '@/i18n';
import {
  useTenantProviders,
  useCreateTenantProvider,
  useUpdateTenantProvider,
  useDeleteTenantProvider,
  useValidateTenantProvider,
} from '../hooks/useTenantProviders';
import { ProviderCard } from '../components/ProviderCard';
import { ProviderForm } from '../components/ProviderForm';
import type { TenantProvider, TenantProviderCreate, TenantProviderUpdate } from '../types';

export function TenantProvidersPage() {
  const { t } = useI18n();
  const [showForm, setShowForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState<TenantProvider | null>(null);
  const [validatingId, setValidatingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Queries and mutations
  const { data: providers, isLoading } = useTenantProviders();
  const createMutation = useCreateTenantProvider();
  const deleteMutation = useDeleteTenantProvider();
  const validateMutation = useValidateTenantProvider();

  // We need to handle update mutation dynamically based on the provider being edited
  const updateMutationFn = useUpdateTenantProvider(editingProvider?.id || '');

  const handleCreate = async (data: TenantProviderCreate | TenantProviderUpdate) => {
    try {
      setError(null);
      await createMutation.mutateAsync(data as TenantProviderCreate);
      setShowForm(false);
      setSuccessMessage(t('admin.llmConfig.tenantProviders.addedSuccess'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleUpdate = async (data: TenantProviderCreate | TenantProviderUpdate) => {
    if (!editingProvider) return;
    
    try {
      setError(null);
      await updateMutationFn.mutateAsync(data as TenantProviderUpdate);
      setEditingProvider(null);
      setSuccessMessage(t('admin.llmConfig.tenantProviders.updatedSuccess'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleValidate = async (id: string) => {
    try {
      setError(null);
      setValidatingId(id);
      const result = await validateMutation.mutateAsync(id);
      if (result.is_valid) {
        setSuccessMessage(t('admin.llmConfig.tenantProviders.validatedSuccess'));
      } else {
        setError(`${t('admin.llmConfig.tenantProviders.validationFailed')}: ${result.error || 'Unknown error'}`);
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setValidatingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setError(null);
      await deleteMutation.mutateAsync(id);
      setSuccessMessage(t('admin.llmConfig.tenantProviders.deletedSuccess'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleEdit = (provider: TenantProvider) => {
    setEditingProvider(provider);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/settings"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('admin.llmConfig.tenantProviders.backToSettings')}
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('admin.llmConfig.tenantProviders.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {t('admin.llmConfig.tenantProviders.description')}
            </p>
          </div>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('admin.llmConfig.tenantProviders.addProvider')}
          </Button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-950/20 rounded-lg text-red-700 dark:text-red-300 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-600 hover:text-red-700"
          >
            ✕
          </button>
        </div>
      )}

      {/* Success message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg text-green-700 dark:text-green-300">
          ✓ {successMessage}
        </div>
      )}

      {/* Provider cards */}
      {providers && providers.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {providers.map((provider) => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              onValidate={handleValidate}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isValidating={validatingId === provider.id}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 px-4 border rounded-lg bg-muted/20">
          <Key className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">{t('admin.llmConfig.tenantProviders.noProviders')}</h3>
          <p className="text-muted-foreground mb-4">
            {t('admin.llmConfig.tenantProviders.noProvidersDescription')}
          </p>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('admin.llmConfig.tenantProviders.addFirst')}
          </Button>
        </div>
      )}

      {/* Info section */}
      <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <h3 className="font-medium text-blue-900 dark:text-blue-100 flex items-center gap-2">
          <Key className="w-5 h-5" />
          {t('admin.llmConfig.tenantProviders.byomTitle')}
        </h3>
        <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
          {t('admin.llmConfig.tenantProviders.byomDescription')}
        </p>
        <ul className="text-sm text-blue-700 dark:text-blue-300 mt-2 list-disc list-inside space-y-1">
          <li>{t('admin.llmConfig.tenantProviders.byomEncrypted')}</li>
          <li>{t('admin.llmConfig.tenantProviders.byomNeverLogged')}</li>
          <li>{t('admin.llmConfig.tenantProviders.byomIsolated')}</li>
        </ul>
      </div>

      {/* Create form dialog */}
      <ProviderForm
        open={showForm}
        onOpenChange={setShowForm}
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />

      {/* Edit form dialog */}
      <ProviderForm
        open={!!editingProvider}
        onOpenChange={(open) => !open && setEditingProvider(null)}
        onSubmit={handleUpdate}
        provider={editingProvider}
        isLoading={updateMutationFn.isPending}
      />
    </div>
  );
}

export default TenantProvidersPage;
